// Settings > Onboarding: everything a brand installs on its store, in one
// tab — the site tag (gt.js), every plan; the Shopify custom pixel, free /
// standard / enterprise brands only (the API says `available`); and one
// Check installation button over both. Replaced the Google Tag tab
// (settings/google-tag redirects here).
import React, { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import axiosInstant from '../../../../api'
import { brandOnboardingStatusEndpoint } from '../../../../api/endpoints'
import { useUser } from '../../../../hooks/useUser'
import { useWorkspaceBrandId } from '../../../../hooks/useWorkspaceBrandId'
import GoogleTag from '../GoogleTag'
import InstallCheck from './InstallCheck'
import ShopifyPixel, { ShopifyPixelState } from './ShopifyPixel'
import ReportStatus from './ReportStatus'
import ConsentSettings, { TagConsentMode } from './ConsentSettings'

interface TagState {
  // What the tag assumes when no consent signal answers.
  consent_mode?: TagConsentMode
  // From the newest evidence: a ping, or the last install check.
  installed: boolean
  last_seen_at: string | null
  hosts: { host: string; last_seen_at: string }[]
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[16px] font-bold text-text-1">{title}</h3>
      {children}
    </section>
  )
}

// `brandId` prop: the internal brand detail page renders the tab for the
// brand it is showing; brand users get theirs from the workspace / membership.
export default function Onboarding({ brandId: brandIdProp = 0 }: { brandId?: number } = {}) {
  const { brand } = useUser()
  const workspaceBrandId = useWorkspaceBrandId()
  const brandId = brandIdProp || workspaceBrandId || (brand && brand[0] && brand[0].id) || 0
  const [tag, setTag] = useState<TagState | null>(null)
  const [pixel, setPixel] = useState<ShopifyPixelState | null>(null)

  const loadStatus = useCallback(async () => {
    if (!brandId) return
    try {
      const { data } = await axiosInstant.get(brandOnboardingStatusEndpoint(brandId))
      setTag(data.tag)
      setPixel(data.shopify)
    } catch {
      toast.error("Couldn't load the install status.")
    }
  }, [brandId])

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  if (!brandId) return null

  return (
    <div className="flex flex-col gap-8">
      <Section title="Check installation">
        <InstallCheck brandId={brandId} onChecked={loadStatus} />
      </Section>
      <Section title="Site tag">
        {tag ? (
          <ReportStatus
            installed={tag.installed}
            lastAt={tag.last_seen_at}
            activeLabel="Tag installed"
            idleLabel={tag.last_seen_at ? 'Not detected' : 'No data yet'}
            detail={tag.hosts.length ? `on ${tag.hosts.map((h) => h.host).join(', ')}` : null}
          />
        ) : null}
        <GoogleTag brandId={brandId} />
      </Section>
      <Section title="Privacy & consent">
        <ConsentSettings brandId={brandId} mode={(tag && tag.consent_mode) || 'regional'} onSaved={loadStatus} />
      </Section>
      {pixel && pixel.available ? (
        <Section title="Shopify custom pixel">
          <ShopifyPixel brandId={brandId} pixel={pixel} onChange={setPixel} />
        </Section>
      ) : null}
    </div>
  )
}
